import {
  CancellationRuleDto,
  GetCancellationRulesRequest,
  GetPropertyByIdRequest,
  GetRoomsByPropertyIdRequest,
  RoomDto,
} from '@/api/generated/fetch-client';
import { NotFoundError } from '@/types/errors';
import { RepositoryException, type WithSignal } from '@/types/typings';
import { formatDateForBackend } from '@/utils/format-date-for-backend';

import type {
  AccommodationCatalogCitiesResponseDto,
  AccommodationCatalogPageResponseDto,
  GetAccommodationCatalogCitiesRequest,
  GetAccommodationCatalogRequest,
} from '@/features/accommodation/types/catalog';
import { AccommodationDetailModel } from '@/features/accommodation-details/types/accommodation-detail-model';

import { AccommodationApi } from '../api';
import { PropertyType } from '../types/ws/websocket-message';

export class AccommodationRepository {
  constructor(private appAccommodationApi: AccommodationApi) {}

  public async getCatalogCities(
    requestParams: WithSignal<GetAccommodationCatalogCitiesRequest>,
  ): Promise<AccommodationCatalogCitiesResponseDto> {
    const response = await this.appAccommodationApi.getCatalogCities(requestParams);
    if (!response) throw new RepositoryException('repository getCatalogCities error');
    return response;
  }

  public async getCatalogPage(
    requestParams: WithSignal<GetAccommodationCatalogRequest>,
  ): Promise<AccommodationCatalogPageResponseDto> {
    const response = await this.appAccommodationApi.getCatalogPage(requestParams);
    if (!response) throw new RepositoryException('repository getCatalogPage error');
    return response;
  }

  public async getCancellationRules(
    requestParams: WithSignal<GetCancellationRulesRequest>,
  ): Promise<CancellationRuleDto> {
    const rules = await this.appAccommodationApi.getCancellationRules(requestParams);

    if (!rules) {
      throw new RepositoryException('repository getCancellationRules error');
    }

    return rules;
  }

  public async getListingById(
    requestParams: WithSignal<GetPropertyByIdRequest>,
    propertyType: PropertyType,
  ): Promise<{ listing: AccommodationDetailModel }> {
    const property = await this.appAccommodationApi.getPropertyById(requestParams);

    if (!property) {
      throw new RepositoryException('repository getListingById error');
    }

    return {
      listing: AccommodationDetailModel.fromDto(property, propertyType),
    };
  }

  public async getRoomsByPropertyId(
    requestParams: WithSignal<GetRoomsByPropertyIdRequest>,
  ): Promise<RoomDto[]> {
    try {
      return await this.appAccommodationApi.getRoomsByPropertyId({
        ...requestParams,
        arrivalDate: formatDateForBackend(requestParams.arrivalDate),
        departureDate: formatDateForBackend(requestParams.departureDate),
      });
    } catch (error) {
      if (error instanceof NotFoundError) {
        return [];
      }
      throw error;
    }
  }
}
